import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "../../assets/img/logos/divixNegro.png";
import XLogo from "../../assets/img/logos/X.svg";
import { Monitor } from "../../components/icons/Monitor";
import { Team } from "../../components/icons/Team";
import { Sucursal } from "../../components/icons/Sucursal";
import { Message } from "../../components/icons/Message";
import { Config } from "../../components/icons/Config";
import { WhatsApp } from "../../components/icons/WhatsApp";
import { useTranslation } from "react-i18next";
import { Group } from "../../components/icons/Group";
import { useToast } from "../../context/toast-context";
import { SidebarSkeleton } from "../../components/skeletons/sidebar-skeleton";
import { Avatar } from "@mui/material";
import { useAuth } from "../../context/auth-context";
import { useUser } from "../../context/user-context";
import { UserDropdown } from "./UserDropdown";
import { useUnreadMessages } from "../../context/unread-messages-context";
import { Manager } from "../../components/icons/Manager";
import { DollarSign } from "../../components/icons/DollarSign";
import profileGenericPic from "../../assets/img/logos/profileGenericPic.jpg";

export function Sidebar() {
  const [expanded, setExpanded] = useState(true);
  const location = useLocation();
  const { user, isLoading } = useUser();
  const { unreadCount } = useUnreadMessages();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { logout } = useAuth();

  const isActive = (path: string) => {
    if (path === "/dashboard") {
      return location.pathname === path;
    }

    return location.pathname.startsWith(path);
  };

  const username = user?.name.length ? user.name.split(" ")[0] : user?.name;
  const superManager = user?.rol === "Manager" && user?.isResponsible;
  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1000px)");
    setExpanded(mediaQuery.matches);

    const handleMediaChange = (e: MediaQueryListEvent) => {
      setExpanded(e.matches);
    };
    mediaQuery.addEventListener("change", handleMediaChange);

    return () => {
      mediaQuery.removeEventListener("change", handleMediaChange);
    };
  }, []);

  if (isLoading) return <SidebarSkeleton />;

  return (
    <aside
      className={`z-50 hidden max-h-screen rounded-3xl p-1 sm:p-4 bg-white transition-all duration-300 ease-in-out sm:flex sm:flex-col ${
        expanded ? "w-52" : "w-10 min-w-fit"
      }`}
    >
      <div className="flex flex-col shrink-0 mb-6">
        <div
          className={
            expanded
              ? "flex h-6 items-center justify-between"
              : "flex flex-col items-center justify-center gap-4"
          }
        >
          {expanded ? (
            <img className="h-full" src={Logo} alt="Divix Logo" />
          ) : (
            <img className="h-6" src={XLogo} alt="Divix Logo" />
          )}
          <button onClick={() => setExpanded((curr) => !curr)}>
            {expanded ? (
              <div className="rotate-180 transform transition-transform ease-linear duration-75">
                <svg
                  fill="#ececec"
                  height="16"
                  width="16"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 330 330"
                  xmlSpace="preserve"
                >
                  <path d="m250.606 154.389-150-149.996c-5.857-5.858-15.355-5.858-21.213.001-5.857 5.858-5.857 15.355.001 21.213l139.393 139.39L79.393 304.394c-5.857 5.858-5.857 15.355.001 21.213C82.322 328.536 86.161 330 90 330s7.678-1.464 10.607-4.394l149.999-150.004a14.996 14.996 0 0 0 0-21.213z" />
                </svg>
              </div>
            ) : (
              <div>
                <svg
                  fill="#ececec"
                  height="16"
                  width="16"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 330 330"
                  xmlSpace="preserve"
                >
                  <path d="m250.606 154.389-150-149.996c-5.857-5.858-15.355-5.858-21.213.001-5.857 5.858-5.857 15.355.001 21.213l139.393 139.39L79.393 304.394c-5.857 5.858-5.857 15.355.001 21.213C82.322 328.536 86.161 330 90 330s7.678-1.464 10.607-4.394l149.999-150.004a14.996 14.996 0 0 0 0-21.213z" />
                </svg>
              </div>
            )}
          </button>
        </div>

        {expanded && (
          <div className="text-wrap mt-4">
            <h2 className="text-2xl font-light text-[#838382] block">
              {t("sidebar.hi")}
            </h2>
            <h2 className="text-2xl font-light text-[#838382] block">
              {username}!
            </h2>
          </div>
        )}
      </div>

      <div
        className={`flex-1 overflow-y-auto ${
          expanded ? "custom-scrollbar-sidebar" : "overflow-hidden"
        } py-2 `}
      >
        <nav className={`flex flex-col gap-5 ${expanded ? "mr-2" : "mr-0"}`}>
          <Link
            className={`inline-flex items-center whitespace-nowrap tracking-wider text-xs font-light transition-colors h-10 rounded-full ${
              isActive("/dashboard")
                ? "text-white bg-black"
                : "text-[#83838b] bg-transparent hover:bg-black/10"
            } ${expanded ? "sm:px-5 justify-start" : "justify-center px-1"}`}
            to="/dashboard"
          >
            <div className={`${expanded && "mr-2"}`}>
              {isActive("/dashboard") ? (
                <Monitor stroke="#ececec" />
              ) : (
                <Monitor stroke="#83838b" />
              )}
            </div>
            {expanded && <span>Dashboard</span>}
          </Link>
          <Link
            className={`inline-flex items-center whitespace-nowrap tracking-wider text-xs font-light transition-colors h-10 rounded-full  ${
              isActive("/dashboard/teams")
                ? "text-white bg-black"
                : "text-[#83838b] bg-transparent hover:bg-black/10"
            } ${expanded ? "sm:px-5 justify-start" : "justify-center px-1"}`}
            to="/dashboard/teams"
          >
            <div className={`${expanded && "mr-2"}`}>
              {isActive("/dashboard/teams") ? (
                <Team stroke="#ececec" />
              ) : (
                <Team stroke="#83838b" />
              )}
            </div>
            {expanded && t("sidebar.team")}
          </Link>
          <Link
            className={`inline-flex items-center whitespace-nowrap tracking-wider text-xs font-light transition-colors h-10 rounded-full ${
              isActive("/dashboard/branches")
                ? "text-white bg-black"
                : "text-[#83838b] bg-transparent hover:bg-black/10"
            } ${expanded ? "sm:px-5 justify-start" : "justify-center px-1"}`}
            to="/dashboard/branches"
          >
            <div className={`${expanded && "mr-2"}`}>
              {isActive("/dashboard/branches") ? (
                <Sucursal fill="#ececec" />
              ) : (
                <Sucursal fill="#83838b" />
              )}
            </div>
            {expanded && t("sidebar.branches")}
          </Link>
          <Link
            className={`inline-flex items-center whitespace-nowrap tracking-wider text-xs font-light transition-colors h-10 rounded-full ${
              isActive("/dashboard/payments")
                ? "text-white bg-black"
                : "text-[#83838b] bg-transparent hover:bg-black/10"
            } ${expanded ? "sm:px-5 justify-start" : "justify-center px-1"}`}
            to="/dashboard/payments"
          >
            <div className={`${expanded && "mr-2"}`}>
              {isActive("/dashboard/payments") ? (
                <DollarSign fill="#ececec" />
              ) : (
                <DollarSign fill="#83838b" />
              )}
            </div>
            {expanded && t("sidebar.payments")}
          </Link>
          {superManager && (
            <Link
              className={`inline-flex items-center whitespace-nowrap tracking-wider text-xs font-light transition-colors h-10 rounded-full ${
                isActive("/dashboard/managers")
                ? "text-white bg-black"
                : "text-[#83838b] bg-transparent hover:bg-black/10"
            } ${expanded ? "sm:px-5 justify-start" : "justify-center px-1"}`}
            to="/dashboard/managers"
          >
            <div className={`${expanded && "mr-2"}`}>
              {isActive("/dashboard/managers") ? (
                <Manager fill="#ececec" />
              ) : (
                <Manager fill="#83838b" />
              )}
            </div>
              {expanded && t("sidebar.managers")}
            </Link>
          )}
          <Link
            className={`inline-flex items-center whitespace-nowrap tracking-wider text-xs font-light transition-colors h-10 rounded-full ${
              isActive("/dashboard/groups")
                ? "text-white bg-black"
                : "text-[#83838b] bg-transparent hover:bg-black/10"
            } ${expanded ? "sm:px-5 justify-start" : "justify-center px-1"}`}
            to="/dashboard/groups"
          >
            <div className={`${expanded && "mr-2"}`}>
              {isActive("/dashboard/groups") ? (
                <Group fill="#ececec" />
              ) : (
                <Group fill="#83838b" />
              )}
            </div>
            {expanded && t("sidebar.groups")}
          </Link>
          <Link
            className={`inline-flex items-center whitespace-nowrap tracking-wider text-xs font-light transition-colors h-10 rounded-full ${
              isActive("/dashboard/messages")
                ? "text-white bg-black"
                : "text-[#83838b] bg-transparent hover:bg-black/10"
            } ${expanded ? "sm:px-5 justify-start" : "justify-center px-1"}`}
            to="/dashboard/messages"
          >
            <div className={`${expanded && "mr-2"} relative`}>
              {isActive("/dashboard/messages") ? (
                <Message stroke="#ececec" />
              ) : (
                <Message stroke="#71717a" />
              )}
              {unreadCount > 0 && (
                <span className="absolute -top-1 -left-1 bg-red text-white text-[11px] rounded-full w-4 h-4 flex items-center justify-center">
                  {unreadCount > 99 ? "99" : unreadCount}
                </span>
              )}
            </div>
            {expanded && t("sidebar.messages")}
          </Link>
          <a
            className={`inline-flex items-center whitespace-nowrap tracking-wider text-xs font-light transition-colors h-10 rounded-full text-[#83838b] bg-transparent hover:bg-black/10 hover:text-black group ${expanded ? "sm:px-5 justify-start" : "justify-center px-1"}`}
            href="https://wa.me/5491171187604"
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className={`${expanded && "mr-2"}`}>
              <WhatsApp fill="#83838b" className="group-hover:fill-black transition-colors" />
            </div>
            {expanded && t("sidebar.support")}
          </a>
          <Link
            className={`inline-flex items-center whitespace-nowrap tracking-wider text-xs font-light transition-colors h-10 rounded-full ${
              isActive("/dashboard/settings")
                ? "text-white bg-black"
                : "text-[#83838b] bg-transparent hover:bg-black/10"
            } ${expanded ? "sm:px-5 justify-start" : "justify-center px-1"}`}
            to="/dashboard/settings"
          >
            <div className={`${expanded && "mr-2"}`}>
              {isActive("/dashboard/settings") ? (
                <Config fill="#ececec" />
              ) : (
                <Config fill="#83838b" />
              )}
            </div>
            {expanded && t("sidebar.settings")}
          </Link>
        </nav>
      </div>

      <div className="mt-6 shrink-0">
        <div role="none" className="shrink-0 bg-gray-light h-[1px] w-full" />
        <div className="flex w-full items-start gap-2 mt-4">
          {expanded ? (
            <>
              <div className="relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full">
                <Avatar
                  sx={{
                    width: "100%",
                    height: "100%",
                  }}
                  src={profileGenericPic}
                  alt="Avatar Usuario"
                />
              </div>

              <div className="flex flex-col items-start justify-center">
                <p className="font-semibold text-sm">{user?.name}</p>
                <button onClick={logout} className="text-xs hover:underline">
                  {t("logout")}
                </button>
              </div>
            </>
          ) : (
            <UserDropdown />
          )}
        </div>
      </div>
    </aside>
  );
}
